import {
  getCohortProps,
  makeReader,
  withErrors,
  type BotDetectionProps,
  type CohortProps,
} from './cohortSignals';

export type { BotDetectionProps } from './cohortSignals';

/** The 3 window globals `sl` probes for. Exported so tests assert the real list. */
export const SELENIUM_WINDOW_PROPS = ['_Selenium_IDE_Recorder', 'callSelenium', '_selenium'];

/** The document keys `sl` probes for. Exported so tests assert the real list. */
export const SELENIUM_DOCUMENT_PROPS = [
  '__webdriver_script_fn',
  '__driver_evaluate',
  '__webdriver_evaluate',
  '__selenium_evaluate',
  '__fxdriver_evaluate',
  '__driver_unwrapped',
  '__webdriver_unwrapped',
  '__selenium_unwrapped',
  '__fxdriver_unwrapped',
  '__webdriver_script_func',
  '$cdc_asdjflasutopfhvcZLmcfl_',
];

/**
 * Heuristic signals used to detect automated/bot browsers, spread into the
 * SIGNUP_BOT_DETECTION event.
 *
 * Always returns a payload. Every read is isolated, because a hardened or instrumented
 * browser can install a throwing getter on any of these properties and that is precisely the
 * case worth reporting. A throw yields the same sentinel the field uses when the API is
 * absent, and appends the field's name to `cf.err` under a `bd.` prefix — so "sentinel
 * because absent" stays distinguishable from "sentinel because it threw". This matters more
 * here than in snapps: two of the three call sites sit next to `SignupApi.createTrial`.
 *
 * Kept in step with the snapps collector at
 * `src/packages/api/telemetry/src/analytics/props.ts`; the emitted JSON must match.
 *
 * @param cohort
 * Source of the cohort bundle. Defaults to the page-load singleton; tests pass a collector
 * with a cold cache so their assertions do not depend on which test ran first.
 */
export function getBotDetectionProps(
  cohort: () => CohortProps = getCohortProps,
): BotDetectionProps {
  const { read, err } = makeReader();

  return {
    wd: read<boolean | null>('bd.wd', null, () =>
      typeof window.navigator.webdriver === 'boolean' ? window.navigator.webdriver : null,
    ),
    pl: read('bd.pl', -1, () => navigator.plugins?.length),
    mtl: read('bd.mtl', -1, () => navigator.mimeTypes?.length),
    sw: read('bd.sw', -1, () => window.screen?.width),
    sh: read('bd.sh', -1, () => window.screen?.height),
    pf: read('bd.pf', '', () => navigator.platform || undefined),
    hwc: read('bd.hwc', -1, () => navigator.hardwareConcurrency || undefined),
    el: read('bd.el', -1, () => eval.toString().length),
    // `sl` degrades to `null`, never `false`: a throwing `has` trap on window/document is
    // itself an automation artifact, so `false` would be fail-open on the signal this field
    // exists to carry.
    sl: read<boolean | null>(
      'bd.sl',
      null,
      () =>
        SELENIUM_WINDOW_PROPS.some(prop => prop in window) ||
        SELENIUM_DOCUMENT_PROPS.some(prop => prop in document),
    ),
    // Cohort / attribution fingerprint. Must stay last so the nine legacy keys keep their
    // position.
    cf: withErrors(cohort(), err),
  };
}
